import type { RequirementStatus } from '@milepost/shared';

import { STATUS_LABEL } from '../lib/format';
import './StatusGlyph.css';

/**
 * A compliance status, shown as a glyph plus a written label.
 *
 * Colour is never the only signal. Crimson, rust and gold are adjacent for a red-green
 * colourblind reader, and here misreading "lapsed" as "expiring" means a driver who is not
 * qualified stays in a truck. So each status also gets a distinct shape:
 *
 *   ✕  lapsed        — a cross: this is closed off, the driver cannot drive
 *   ○  missing       — an empty ring: nothing is there
 *   ◑  expiring      — half-filled: running down
 *   ◆  needs review  — a diamond: a decision is waiting on a person
 *   ✓  on file       — a check
 *   –  n/a           — a dash: deliberately nothing to do
 */

const GLYPH: Record<RequirementStatus, string> = {
  expired: '✕',
  missing: '○',
  expiring_soon: '◑',
  pending_review: '◆',
  satisfied: '✓',
  not_applicable: '–',
};

interface Props {
  status: RequirementStatus;
  /** `chip` for a filled pill, `inline` for a glyph and label in running text. */
  variant?: 'chip' | 'inline' | 'glyph';
}

export function StatusGlyph({ status, variant = 'chip' }: Props) {
  const label = STATUS_LABEL[status];

  if (variant === 'glyph') {
    return (
      <span className={`sg sg-glyph st-${status}`} title={label}>
        <span aria-hidden="true">{GLYPH[status]}</span>
        <span className="sr-only">{label}</span>
      </span>
    );
  }

  return (
    <span className={`sg sg-${variant} st-${status}`}>
      <span className="sg-mark" aria-hidden="true">
        {GLYPH[status]}
      </span>
      {label}
    </span>
  );
}
